<template>
  <v-container fluid>
    <v-row>
      <!-- Header -->
      <v-col cols="12">
        <h1 class="text-h4 mb-2">
          <v-icon class="mr-2">mdi-cart</v-icon>
          Comprar {{ coin }}
        </h1>
        <p class="text-subtitle-1 text-medium-emphasis">
          Token Sale — Rede de Testes (pagamento simulado)
        </p>
      </v-col>
    </v-row>

    <v-row>
      <!-- Purchase Form -->
      <v-col cols="12" md="7">
        <v-card elevation="2">
          <v-card-title>
            <v-icon class="mr-2">mdi-cash-register</v-icon>
            Nova Compra
          </v-card-title>
          <v-card-text>
            <!-- Wallet Status -->
            <v-alert
              v-if="!walletConnected"
              type="warning"
              variant="tonal"
              class="mb-4"
              icon="mdi-wallet-outline"
            >
              Conecte sua carteira para comprar {{ coin }}.
            </v-alert>

            <v-alert
              v-else
              type="success"
              variant="tonal"
              class="mb-4"
              density="compact"
              icon="mdi-wallet"
            >
              Carteira: {{ walletAddressShort }}
            </v-alert>

            <!-- Currency Selector -->
            <v-select
              v-model="selectedCurrency"
              :items="currencies"
              item-title="label"
              item-value="code"
              label="Moeda de pagamento"
              variant="outlined"
              class="mb-2"
              return-object
            >
              <template v-slot:item="{ item, props }">
                <v-list-item v-bind="props">
                  <template v-slot:prepend>
                    <v-icon>{{ item.raw.icon }}</v-icon>
                  </template>
                </v-list-item>
              </template>
              <template v-slot:selection="{ item }">
                <v-icon class="mr-2">{{ item.raw.icon }}</v-icon>
                {{ item.raw.label }}
              </template>
            </v-select>

            <!-- Amount Input -->
            <v-text-field
              v-model.number="paymentAmount"
              :label="`Quantidade de ${selectedCurrency.code.toUpperCase()} a gastar`"
              type="number"
              variant="outlined"
              min="0"
              step="0.01"
              :hint="`Taxa: 1 ${selectedCurrency.code.toUpperCase()} = ${selectedCurrency.rate.toLocaleString()} ${coin}`"
              persistent-hint
              class="mb-2"
            />

            <!-- Live Calculation -->
            <v-card variant="tonal" color="primary" class="mb-4 pa-3">
              <div class="d-flex justify-space-between align-center">
                <span class="text-subtitle-1">{{ t('sale.youReceive') }}</span>
                <span class="text-h5 font-weight-bold">
                  {{ calculatedKinx.toLocaleString(undefined, { maximumFractionDigits: 2 }) }} {{ coin }}
                </span>
              </div>
            </v-card>

            <!-- Buy Button -->
            <v-btn
              color="success"
              size="large"
              block
              :loading="purchasing"
              :disabled="!canPurchase"
              @click="buyKinx"
            >
              <v-icon start>mdi-cart-check</v-icon>
              Comprar {{ coin }}
            </v-btn>
          </v-card-text>
        </v-card>

        <!-- Transaction Result -->
        <v-card v-if="lastResult" elevation="2" class="mt-4">
          <v-card-title>
            <v-icon class="mr-2" color="success">mdi-check-circle</v-icon>
            Compra Realizada
          </v-card-title>
          <v-card-text>
            <v-table density="compact">
              <tbody>
                <tr>
                  <td class="font-weight-bold">{{ coin }} Recebido</td>
                  <td>{{ lastResult.kinx_amount_display?.toLocaleString() }} {{ coin }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">{{ t('sale.payment') }}</td>
                  <td>{{ lastResult.payment_amount }} {{ lastResult.payment_currency?.toUpperCase() }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">{{ t('sale.newBalance') }}</td>
                  <td>{{ lastResult.new_balance_display?.toLocaleString() }} {{ coin }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">{{ t('sale.exchangeRate') }}</td>
                  <td>1 {{ lastResult.payment_currency?.toUpperCase() }} = {{ lastResult.exchange_rate?.toLocaleString() }} {{ coin }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>

        <!-- Error -->
        <v-alert v-if="errorMsg" type="error" variant="tonal" class="mt-4" closable @click:close="errorMsg = ''">
          {{ errorMsg }}
        </v-alert>
      </v-col>

      <!-- Exchange Rate Table -->
      <v-col cols="12" md="5">
        <!-- Sale Pool Status -->
        <v-card elevation="2" class="mb-4">
          <v-card-title>
            <v-icon class="mr-2">mdi-chart-donut</v-icon>
            Status do Token Sale
          </v-card-title>
          <v-card-text>
            <div v-if="saleStatus">
              <v-chip
                :color="saleStatus.status === 'active' ? 'success' : 'error'"
                class="mb-3"
              >
                {{ saleStatus.status === 'active' ? 'Ativo' : 'Encerrado' }}
              </v-chip>

              <!-- Progress Bar -->
              <div class="mb-3">
                <div class="d-flex justify-space-between text-caption mb-1">
                  <span>{{ t('sale.sold') }}</span>
                  <span>{{ saleProgressPercent.toFixed(1) }}%</span>
                </div>
                <v-progress-linear
                  :model-value="saleProgressPercent"
                  color="primary"
                  height="12"
                  rounded
                />
              </div>

              <v-table density="compact">
                <tbody>
                  <tr>
                    <td class="font-weight-bold">{{ t('sale.remaining') }}</td>
                    <td>{{ formatKinx(saleStatus.remaining_nanokinx) }} {{ saleCurrency }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">{{ t('sale.totalSold') }}</td>
                    <td>{{ formatKinx(saleStatus.total_sold_nanokinx) }} {{ saleCurrency }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">{{ t('sale.poolAddress') }}</td>
                    <td class="text-truncate" style="max-width: 200px">
                      {{ saleStatus.sale_pool_address ? saleStatus.sale_pool_address.slice(0, 12) + '...' : '-' }}
                    </td>
                  </tr>
                </tbody>
              </v-table>

              <v-btn variant="text" size="small" class="mt-2" @click="loadSaleStatus" :loading="loadingSaleStatus">
                <v-icon start>mdi-refresh</v-icon>
                Atualizar
              </v-btn>
            </div>
            <div v-else class="text-center pa-4 text-medium-emphasis">
              <v-progress-circular v-if="loadingSaleStatus" indeterminate size="24" />
              <span v-else>{{ t('app.noData') }}</span>
            </div>
          </v-card-text>
        </v-card>

        <v-card elevation="2">
          <v-card-title>
            <v-icon class="mr-2">mdi-chart-bar</v-icon>
            Taxas de Câmbio (Testnet)
          </v-card-title>
          <v-card-text>
            <v-table>
              <thead>
                <tr>
                  <th>Moeda</th>
                  <th class="text-right">{{ coin }} por unidade</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="c in currencies" :key="c.code">
                  <td>
                    <v-icon class="mr-1" size="small">{{ c.icon }}</v-icon>
                    {{ c.label }}
                  </td>
                  <td class="text-right font-weight-bold">{{ c.rate.toLocaleString() }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>

        <!-- Current Balance -->
        <v-card elevation="2" class="mt-4">
          <v-card-title>
            <v-icon class="mr-2">mdi-wallet</v-icon>
            Saldo Atual
          </v-card-title>
          <v-card-text>
            <div v-if="walletConnected" class="text-center">
              <p class="text-h4 font-weight-bold text-primary">
                {{ currentBalance.toLocaleString(undefined, { maximumFractionDigits: 2 }) }}
              </p>
              <p class="text-subtitle-1 text-medium-emphasis">{{ coin }}</p>
              <v-btn variant="text" size="small" @click="refreshBalance" :loading="loadingBalance">
                <v-icon start>mdi-refresh</v-icon>
                Atualizar
              </v-btn>
            </div>
            <div v-else class="text-center text-medium-emphasis pa-4">
              Conecte sua carteira para ver o saldo
            </div>
          </v-card-text>
        </v-card>

        <!-- Info -->
        <v-card elevation="2" class="mt-4" variant="tonal" color="info">
          <v-card-text>
            <p class="text-subtitle-2 mb-1"><v-icon size="small">mdi-information</v-icon> Informações</p>
            <ul class="text-body-2">
              <li>Máximo por compra: 1.000.000 {{ coin }}</li>
              <li>Cooldown: 10 segundos entre compras</li>
              <li>Pagamentos simulados (testnet)</li>
              <li>Sem verificação de pagamento real</li>
            </ul>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { ExplorerAPI, API_BASE_URL } from '@/services/api'
import { useWalletStore } from '@/stores/wallet'

export default {
  name: 'TokenSaleView',
  data() {
    return {
      selectedCurrency: { code: 'usdt', label: 'USDT (Tether)', icon: 'mdi-currency-usd', rate: 100 },
      currencies: [
        { code: 'usdt', label: 'USDT (Tether)', icon: 'mdi-currency-usd', rate: 100 },
        { code: 'eth', label: 'ETH (Ethereum)', icon: 'mdi-ethereum', rate: 250000 },
        { code: 'bnb', label: 'BNB (Binance)', icon: 'mdi-alpha-b-circle', rate: 50000 },
        { code: 'trx', label: 'TRX (Tron)', icon: 'mdi-alpha-t-circle', rate: 10 },
      ],
      paymentAmount: 1,
      purchasing: false,
      lastResult: null,
      errorMsg: '',
      currentBalance: 0,
      loadingBalance: false,
      saleStatus: null,
      loadingSaleStatus: false,
    }
  },
  computed: {
    walletStore() {
      return useWalletStore()
    },
    walletConnected() {
      return this.walletStore.isConnected
    },
    walletAddress() {
      return this.walletStore.address || ''
    },
    walletAddressShort() {
      const a = this.walletAddress
      return a ? `${a.slice(0, 8)}...${a.slice(-8)}` : ''
    },
    calculatedKinx() {
      if (!this.paymentAmount || this.paymentAmount <= 0) return 0
      return this.paymentAmount * (this.selectedCurrency?.rate || 100)
    },
    canPurchase() {
      return this.walletConnected && this.paymentAmount > 0 && !this.purchasing
    },
    saleProgressPercent() {
      if (!this.saleStatus) return 0
      const total = this.saleStatus.remaining_nanokinx + this.saleStatus.total_sold_nanokinx
      if (total === 0) return 0
      return (this.saleStatus.total_sold_nanokinx / total) * 100
    },
    saleCurrency() {
      return this.saleStatus?.currency || (window.__QUARNX_COIN__ || 'tKinX')
    },
  },
  methods: {
    formatKinx(nanokinx) {
      if (!nanokinx) return '0'
      return (nanokinx / 1_000_000_000).toLocaleString(undefined, { maximumFractionDigits: 2 })
    },
    async loadSaleStatus() {
      this.loadingSaleStatus = true
      try {
        this.saleStatus = await ExplorerAPI.tokenSale.status()
      } catch { /* ignore */ }
      finally { this.loadingSaleStatus = false }
    },
    async buyKinx() {
      this.purchasing = true
      this.errorMsg = ''
      this.lastResult = null
      try {
        const result = await ExplorerAPI.tokenSale.comprar(
          this.walletAddress,
          0,
          this.selectedCurrency.code,
          this.paymentAmount
        )
        if (result.error) {
          this.errorMsg = result.error
        } else {
          this.lastResult = result
          this.currentBalance = (result.new_balance || 0) / 1_000_000_000
        }
      } catch (e) {
        this.errorMsg = e?.message || 'Erro ao processar compra'
      } finally {
        this.purchasing = false
      }
    },
    async refreshBalance() {
      if (!this.walletAddress) return
      this.loadingBalance = true
      try {
        await this.walletStore.fetchBalance()
        this.currentBalance = (this.walletStore.balance || 0) / 1_000_000_000
      } catch { /* ignore */ }
      finally { this.loadingBalance = false }
    },
  },
  mounted() {
    this.loadSaleStatus()
    if (this.walletConnected) {
      this.refreshBalance()
    }
  },
}
</script>
